import { useEffect, useRef, useState } from "react";
import { FormProvider, useFieldArray, useForm } from "react-hook-form";
import BusinessDocumentSelectorFormRow, {
  type BusinessDocumentSelectorHandle,
} from "../../BusinessDocuments/BusinessDocumentSelector/BusinessDocumentSelectorFormRow";
import type businessDocumentVM from "../../BusinessDocuments/BusinessDocumentSelector/businessDocumentVM";
import selectorSection from "../../BusinessDocuments/BusinessDocumentSelector/selectorSection";
import getClassMetadata from "../../Common/classMetadataService";
import { AlertDialog } from "../../Common/Dialog/AlertDialog";
import { ConfirmDialog } from "../../Common/Dialog/ConfirmDialog";
import CheckBoxFormRow from "../../Common/EditForm/CheckBox/CheckBoxFormRow";
import getTextAreaMetadata from "../../Common/EditForm/ClassMetadata/getTextAreaMetadata";
import FormButtons from "../../Common/EditForm/formButtons";
import SaveError from "../../Common/EditForm/SaveError";
import TextAreaSection from "../../Common/EditForm/TextArea/TextAreaSection";
import { httpPostNoReturn } from "../../Common/Http/httpPostNoReturn";
import LoadingContentWrapper from "../../Common/Loading/LoadingContentWrapper";
import ContractWithdrawalItemForm from "./ContractWithdrawalItemForm";
import ContractWithdrawalItemDisplayFragment from "./controls/ContractWithdrawalItemDisplayFragment";
import SaveSuccess from "./controls/SaveSuccess";
import type { ContractWithdrawalItemViewModel } from "./Vm/ContractWithdrawalItemViewModel";
import type { ContractWithdrawalRequestServerVM } from "./Vm/ContractWithdrawalRequestServerVM";
import { type ViewModel, viewModelToServerModel } from "./Vm/ViewModel";
import getViewModelNewValues from "./Vm/ViewModelNewValues";

export default function ContractWithdrawalForm(props: {
  navigate: (url: string) => void;
  backUrl?: string;
}) {
  const [isLoading, setIsLoading] = useState(true);
  const [isSaveError, setIsSaveError] = useState(false);
  const [isSaveSuccessful, setIsSaveSuccessful] = useState(false);
  // biome-ignore lint/suspicious/noExplicitAny: metadata is server-dependent and has different properties for different field types
  const [metadata, setMetadata] = useState<{ [key: string]: any }>({});
  const [isItemFormShown, setIsItemFormShown] = useState(false);
  const [editedItem, setEditedItem] =
    useState<ContractWithdrawalItemViewModel | null>(null);
  const [editedItemIndex, setEditedItemIndex] = useState(0);
  const [isDocumentSelected, setIsDocumentSelected] = useState(false);

  const methods = useForm<ViewModel>({
    mode: "onTouched",
    defaultValues: getViewModelNewValues(),
  });

  const { handleSubmit, formState, control, getValues, reset } = methods;
  const { isSubmitting } = formState;

  const itemsMethods = useFieldArray<ViewModel>({
    control,
    name: "items",
  });

  const areThereAnyItems = itemsMethods.fields.length > 0;

  const businessDocumentSelectorRef =
    useRef<BusinessDocumentSelectorHandle>(null);

  // biome-ignore lint/correctness/useExhaustiveDependencies: props.navigate is recreated by the route wrapper; adding it would repeatedly initialize and reset the form
  useEffect(() => {
    // Prevent work after this component is unmounted. The cleanup sets this to
    // false when, for example, the user navigates away before metadata loading finishes.
    let isMounted = true;

    const initialize = async () => {
      try {
        const metadataResponse = await getClassMetadata(
          "ContractWithdrawalVM",
        );
        if (!isMounted) return;

        setMetadata(metadataResponse);
        reset(getViewModelNewValues());
        setIsLoading(false);
      } catch (error) {
        if (!isMounted) return;

        console.error(error);
        props.navigate("/server-communication-error");
      }
    };

    initialize();

    return () => {
      isMounted = false;
    };
    // props.navigate is supplied by a wrapper as a new function on every render;
    // including it here would repeat initialization after each state update.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reset]);

  const onSubmit = async (values: ViewModel) => {
    if (
      !values.isConsumerOrPrivilegedEntrepreneurConfirmed ||
      !values.invoice
    ) {
      return;
    }

    if (values.items.length === 0) {
      await AlertDialog(
        "Brak pozycji",
        "Musisz wprowadzić co najmniej jedną pozycję odstąpienia od umowy.",
      );
      return;
    }

    const lineNumbers = values.items
      .map((item) => item.documentItem?.lineNumber)
      .filter((lineNumber) => lineNumber !== undefined);
    const hasDuplicates = new Set(lineNumbers).size !== lineNumbers.length;

    if (hasDuplicates) {
      await AlertDialog(
        "Powtarzające się pozycje faktury",
        "Każda pozycja odstąpienia od umowy musi odnosić się do innej pozycji faktury.\r\n" +
          "Połącz powtarzające się pozycje lub usuń je.",
      );
      return;
    }

    const confirm = await ConfirmDialog(
      "Potwierdzenie wysyłania formularza",
      "Czy chcesz wysłać oświadczenie o odstąpieniu od umowy?",
    );

    if (confirm === false) return;

    const data = viewModelToServerModel(values);

    try {
      await httpPostNoReturn<ContractWithdrawalRequestServerVM>(
        "/api/ContractWithdrawals/ContractWithdrawal",
        data,
      );
      setIsSaveSuccessful(true);
    } catch {
      setIsSaveError(true);
    }
  };

  const onCancel = async () => {
    const result = await ConfirmDialog(
      "Anulowanie wysyłania",
      "Czy na pewno chcesz porzucić ten formularz?\r\nWybierz OK aby porzucić formularz.",
    );
    if (result === true) {
      props.navigate(props.backUrl || "/");
    }
  };

  const onRemoveItem = async (index: number) => {
    const result = await ConfirmDialog(
      "Usunięcie pozycji",
      `Czy chcesz usunąć pozycję ${index + 1} z formularza?`,
    );
    if (result === true) {
      itemsMethods.remove(index);
    }
  };

  const onEditItem = (index: number) => {
    setEditedItem(getValues("items")[index]);
    setEditedItemIndex(index);
    setIsItemFormShown(true);
  };

  const onNewItem = () => {
    setEditedItem(null);
    setIsItemFormShown(true);
  };

  const onItemSave = (values: ContractWithdrawalItemViewModel) => {
    if (editedItem !== null) {
      itemsMethods.update(editedItemIndex, values);
    } else {
      itemsMethods.append(values);
    }
    setIsItemFormShown(false);
  };

  return (
    <>
      {!isItemFormShown && (
        <div className="content-wrapper">
          <h1 className="border-bottom pb-2 mt-4 mb-5">
            Odstąpienie od umowy
          </h1>
          <LoadingContentWrapper isLoading={isLoading}>
            {!isLoading && !isSaveError && !isSaveSuccessful && (
              <FormProvider {...methods}>
                <form noValidate>
                  <div className="row mb-4">
                    <div className="offset-sm-4 col-sm-8">
                      <div
                        className="alert alert-primary d-flex align-items-center"
                        role="alert"
                      >
                        <div className="flex-shrink-0 ms-3 me-4">
                          <i className="bi bi-lightbulb"></i>
                        </div>
                        <div>
                          Niniejszy formularz umożliwia zgłoszenie odstąpienia
                          od umowy sprzedaży wyłącznie przez Konsumenta lub
                          Przedsiębiorcę z uprawnieniami Konsumenta, tj. osobę
                          fizyczną, która dokonała zakupu niezwiązanego
                          bezpośrednio z jej działalnością gospodarczą lub
                          zawodową albo zakupu związanego z działalnością
                          gospodarczą, lecz niemającego dla niej charakteru
                          zawodowego (art. 22¹ Kodeksu cywilnego oraz art. 7aa
                          ustawy o prawach konsumenta).
                        </div>
                      </div>
                    </div>
                  </div>

                  <CheckBoxFormRow
                    name="isConsumerOrPrivilegedEntrepreneurConfirmed"
                    id="isConsumerOrPrivilegedEntrepreneurConfirmed"
                    label="Potwierdzenie *"
                    longDescription={undefined}
                    inlineDescription={undefined}
                    bodyText="Potwierdzam, że jestem Konsumentem lub Przedsiębiorcą z uprawnieniami Konsumenta."
                    control={control}
                    rules={{
                      validate: {
                        isChecked: (value: unknown) =>
                          value === true
                            ? undefined
                            : "Musisz zaznaczyć to pole.",
                      },
                    }}
                  />

                  <BusinessDocumentSelectorFormRow
                    label="Numer faktury *"
                    name="invoice"
                    id="invoice"
                    control={control}
                    longDescription="Numer faktury, której dotyczy odstąpienie od umowy (wymagane)."
                    inlineDescription={undefined}
                    required={true}
                    selectorSectionId={selectorSection.contractWithdrawal}
                    ref={businessDocumentSelectorRef}
                    locked={areThereAnyItems}
                    onDocumentChanged={(
                      businessDocument: businessDocumentVM,
                    ) => setIsDocumentSelected(!!businessDocument)}
                  />

                  {areThereAnyItems && (
                    <div className="row mb-1">
                      <div className="col-sm-8 offset-sm-4">
                        <div
                          className="alert alert-primary d-flex align-items-center"
                          role="alert"
                        >
                          <div className="flex-shrink-0 ms-3 me-4">
                            <i className="bi bi-lightbulb"></i>
                          </div>
                          <div>
                            Nie możesz zmienić faktury, ponieważ dodałeś już
                            pozycję odstąpienia od umowy odnoszącą się do niej.
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  <hr />
                  <h2
                    className="mb-3"
                    title="Poniżej wprowadź pozycje faktury, których dotyczy odstąpienie od umowy."
                  >
                    Pozycje
                    <sup>
                      <i className="bi bi-question-circle -app-h2-section-label-help"></i>
                    </sup>
                  </h2>

                  <ContractWithdrawalItemDisplayFragment
                    items={
                      itemsMethods.fields as ContractWithdrawalItemViewModel[]
                    }
                    onRemoveItem={onRemoveItem}
                    onEditItem={onEditItem}
                    onNewItem={onNewItem}
                    isNewItemEnabled={isDocumentSelected}
                    onOpenDocumentSelector={() =>
                      businessDocumentSelectorRef.current?.openSelector()
                    }
                  />

                  <hr />
                  <TextAreaSection
                    {...getTextAreaMetadata<ViewModel>(
                      "comments",
                      metadata["comments"],
                    )}
                    rows={5}
                    label="Uwagi"
                    longDescription="Możesz dodać uwagi do całego odstąpienia od umowy."
                    control={control}
                    inlineDescription={undefined}
                    required={false}
                  />

                  <hr />
                  <div className="row pt-2">
                    <div className="offset-sm-4 col-sm-8">
                      <FormButtons
                        onSave={(e) => handleSubmit(onSubmit)(e)}
                        onCancel={onCancel}
                        isSubmitting={isSubmitting}
                        saveText="Potwierdź odstąpienie od umowy"
                        okButtonIconClass="bi-send"
                      />
                    </div>
                  </div>
                </form>
              </FormProvider>
            )}
            {isSaveError && (
              <SaveError
                navigate={props.navigate}
                backUrl={props.backUrl || "/"}
              />
            )}
            {isSaveSuccessful && (
              <SaveSuccess done={() => props.navigate("/")} />
            )}
          </LoadingContentWrapper>
        </div>
      )}

      {isItemFormShown && (
        <ContractWithdrawalItemForm
          values={editedItem}
          onOk={onItemSave}
          onCancel={() => setIsItemFormShown(false)}
          documentNumber={getValues("invoice")?.documentNumber || ""}
          navigate={props.navigate}
        />
      )}
    </>
  );
}
